import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, BriefcaseBusiness, Loader2, RefreshCw, Activity, ArrowRight } from 'lucide-react';
import { apiFetch } from '@/lib/api';

interface TeamspaceCounts {
  accounts: number;
  workspaces: number;
  riskyWorkspaces: number;
  criticalWorkspaces: number;
}

interface TeamspaceOverview {
  counts: TeamspaceCounts;
  recentWorkspaces: Array<Record<string, unknown>>;
  recentEvents: Array<Record<string, unknown>>;
}

interface TeamAccount {
  id: string;
  account_name: string;
  account_code: string | null;
  lifecycle_stage: string;
  status: string;
  summary: string | null;
  linked_tenant_id: string | null;
  updated_at: string;
}

interface TeamWorkspace {
  id: string;
  account_id: string;
  workspace_name: string;
  workspace_type: string;
  stage: string;
  priority: string;
  health_status: string;
  account_name?: string;
  summary: string | null;
  updated_at: string;
}

const STAGES = ['PROSPECT', 'QUALIFIED', 'SOLUTIONING', 'PM_HANDOVER', 'IMPLEMENTATION', 'UAT_GOLIVE', 'HYPERCARE'] as const;

export default function TeamspacePage() {
  const [overview, setOverview] = useState<TeamspaceOverview | null>(null);
  const [accounts, setAccounts] = useState<TeamAccount[]>([]);
  const [workspaces, setWorkspaces] = useState<TeamWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accountForm, setAccountForm] = useState({ accountName: '', accountCode: '', lifecycleStage: 'PROSPECT', summary: '' });
  const [workspaceForm, setWorkspaceForm] = useState({ accountId: '', workspaceName: '', stage: 'PROSPECT', summary: '' });

  async function load() {
    setLoading(true);
    try {
      const [overviewData, accountRows, workspaceRows] = await Promise.all([
        apiFetch<TeamspaceOverview>('/teamspace/overview'),
        apiFetch<TeamAccount[]>('/teamspace/accounts?limit=50'),
        apiFetch<TeamWorkspace[]>('/teamspace/workspaces?limit=50'),
      ]);
      setOverview(overviewData);
      setAccounts(accountRows);
      setWorkspaces(workspaceRows);
      setWorkspaceForm((prev) => ({
        ...prev,
        accountId: prev.accountId || accountRows[0]?.id || '',
      }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function submitAccount(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiFetch('/teamspace/accounts', {
        method: 'POST',
        body: JSON.stringify({
          accountName: accountForm.accountName,
          accountCode: accountForm.accountCode || undefined,
          lifecycleStage: accountForm.lifecycleStage,
          summary: accountForm.summary || undefined,
        }),
      });
      setAccountForm({ accountName: '', accountCode: '', lifecycleStage: 'PROSPECT', summary: '' });
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  async function submitWorkspace(e: FormEvent) {
    e.preventDefault();
    if (!workspaceForm.accountId) return;
    setSubmitting(true);
    try {
      await apiFetch('/teamspace/workspaces', {
        method: 'POST',
        body: JSON.stringify({
          accountId: workspaceForm.accountId,
          workspaceName: workspaceForm.workspaceName,
          stage: workspaceForm.stage,
          summary: workspaceForm.summary || undefined,
        }),
      });
      setWorkspaceForm((prev) => ({ ...prev, workspaceName: '', summary: '' }));
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-slate-500">
            <Building2 className="h-3.5 w-3.5" />
            Boox Teamspace
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Internal lifecycle operating system</h1>
          <p className="max-w-3xl text-sm leading-6 text-slate-600">
            把 prospect、solutioning、handover、implementation、rollout 与 hypercare 收到一个内部工作台。
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <Link
            to="/admin/ceo-desk"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            CEO Desk
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <section className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Accounts', value: overview?.counts.accounts ?? 0, tone: 'bg-slate-50 text-slate-900' },
          { label: 'Workspaces', value: overview?.counts.workspaces ?? 0, tone: 'bg-blue-50 text-blue-900' },
          { label: 'Risky', value: overview?.counts.riskyWorkspaces ?? 0, tone: 'bg-amber-50 text-amber-900' },
          { label: 'Critical', value: overview?.counts.criticalWorkspaces ?? 0, tone: 'bg-rose-50 text-rose-900' },
        ].map((card) => (
          <div key={card.label} className={`rounded-2xl border border-slate-200 p-5 ${card.tone}`}>
            <div className="text-xs font-medium uppercase tracking-[0.22em] opacity-70">{card.label}</div>
            <div className="mt-3 text-3xl font-semibold">{card.value}</div>
          </div>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <BriefcaseBusiness className="h-4 w-4" />
              Recent Workspaces
            </div>
            <div className="mt-4 space-y-3">
              {workspaces.slice(0, 8).map((workspace) => (
                <div key={workspace.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <Link to={`/admin/teamspace/workspaces/${workspace.id}`} className="font-medium text-slate-900 hover:text-slate-700">
                        {workspace.workspace_name}
                      </Link>
                      <div className="text-sm text-slate-500">{workspace.account_name ?? 'Unlinked account'} · {workspace.workspace_type}</div>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">{workspace.stage}</span>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">{workspace.health_status}</span>
                    </div>
                  </div>
                  {workspace.summary ? <p className="mt-3 text-sm leading-6 text-slate-600">{workspace.summary}</p> : null}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Activity className="h-4 w-4" />
              Recent Stage Events
            </div>
            <div className="mt-4 space-y-3">
              {(overview?.recentEvents ?? []).slice(0, 8).map((event) => (
                <div key={String(event.id)} className="rounded-2xl border border-slate-200 p-4">
                  <div className="font-medium text-slate-900">{String(event.workspace_name ?? 'Workspace event')}</div>
                  <div className="mt-1 text-sm text-slate-500">{String(event.event_type ?? 'event')} · {String(event.from_stage ?? '-')} → {String(event.to_stage ?? '-')}</div>
                  {event.note ? <p className="mt-2 text-sm leading-6 text-slate-600">{String(event.note)}</p> : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <form onSubmit={(e) => void submitAccount(e)} className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900">Create account</h2>
            <div className="mt-4 space-y-3">
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Account name" value={accountForm.accountName} onChange={(e) => setAccountForm((prev) => ({ ...prev, accountName: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Account code" value={accountForm.accountCode} onChange={(e) => setAccountForm((prev) => ({ ...prev, accountCode: e.target.value }))} />
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={accountForm.lifecycleStage} onChange={(e) => setAccountForm((prev) => ({ ...prev, lifecycleStage: e.target.value }))}>
                {STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
              </select>
              <textarea className="min-h-[96px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Summary" value={accountForm.summary} onChange={(e) => setAccountForm((prev) => ({ ...prev, summary: e.target.value }))} />
              <button disabled={submitting || !accountForm.accountName.trim()} className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                Create account
              </button>
            </div>
          </form>

          <form onSubmit={(e) => void submitWorkspace(e)} className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900">Create workspace</h2>
            <div className="mt-4 space-y-3">
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={workspaceForm.accountId} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, accountId: e.target.value }))}>
                <option value="">Select account</option>
                {accounts.map((account) => <option key={account.id} value={account.id}>{account.account_name}</option>)}
              </select>
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Workspace name" value={workspaceForm.workspaceName} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, workspaceName: e.target.value }))} />
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={workspaceForm.stage} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, stage: e.target.value }))}>
                {STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
              </select>
              <textarea className="min-h-[96px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Workspace summary" value={workspaceForm.summary} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, summary: e.target.value }))} />
              <button disabled={submitting || !workspaceForm.accountId || !workspaceForm.workspaceName.trim()} className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                Create workspace
              </button>
            </div>
          </form>

          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900">Accounts</h2>
            <div className="mt-4 space-y-3">
              {accounts.slice(0, 8).map((account) => (
                <div key={account.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-slate-900">{account.account_name}</div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">{account.lifecycle_stage}</span>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">{account.status}</span>
                    </div>
                  </div>
                  {account.summary ? <p className="mt-2 text-sm leading-6 text-slate-600">{account.summary}</p> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
